const INSTALLATION_DELIVERY_FEE_VARIANT_ID = 43515513372762;
const INSTALLATION_DELIVERY_FEE_NOTE = 'Delivery, installation & collection fee for <6 mon rental';

let isSyncingInstallationFee = false;
let pendingInstallationFeeSync = null;

function isInstallationDeliveryFeeItem(item) {
  return (
    Number(item?.id) === INSTALLATION_DELIVERY_FEE_VARIANT_ID ||
    Number(item?.variant_id) === INSTALLATION_DELIVERY_FEE_VARIANT_ID
  );
}

function normalizeInstallationFeeText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

function getInstallationFeeItemText(item) {
  const optionText = Array.isArray(item?.options_with_values)
    ? item.options_with_values.map((option) => `${option.name || ''} ${option.value || ''}`).join(' ')
    : '';

  const propertiesText =
    item?.properties && typeof item.properties === 'object'
      ? Object.entries(item.properties)
          .map(([key, value]) => `${key} ${value}`)
          .join(' ')
      : '';

  return normalizeInstallationFeeText(
    [item?.title, item?.product_title, item?.variant_title, optionText, propertiesText].join(' ')
  );
}

function isOneToFiveMonthRentalItem(item) {
  if (isInstallationDeliveryFeeItem(item)) return false;

  const text = getInstallationFeeItemText(item);

  return [
    '1–5',
    '1-5',
    '1 – 5',
    '1 - 5',
    '1–5 months',
    '1-5 months',
    '1–5 month',
    '1-5 month',
    '1–5m',
    '1-5m',
  ].some((pattern) => text.includes(pattern));
}

async function getInstallationFeeCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch cart');
  }

  return response.json();
}

async function addInstallationDeliveryFee() {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items: [
        {
          id: INSTALLATION_DELIVERY_FEE_VARIANT_ID,
          quantity: 1,
          properties: {
            Note: INSTALLATION_DELIVERY_FEE_NOTE,
            _fee_type: 'installation_delivery',
            _reason: 'Rental duration under 6 months',
          },
        },
      ],
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to add installation and delivery fee');
  }
}

function hasExpectedInstallationFeeProperties(item) {
  return item?.properties?.Note === INSTALLATION_DELIVERY_FEE_NOTE;
}

async function changeInstallationFeeLine(line, quantity, sectionsToUpdate = []) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      line,
      quantity,
      ...(sectionsToUpdate.length
        ? {
            sections: sectionsToUpdate.join(','),
            sections_url: window.location.pathname,
          }
        : {}),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to update installation and delivery fee');
  }

  return response.json();
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);
}

function dispatchInstallationFeeCartUpdate(cart) {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'installation-delivery-fee-sync',
        data: {
          source: 'installation-delivery-fee-sync',
          itemCount: cart?.item_count || 0,
        },
      },
    })
  );

  document.dispatchEvent(
    new CustomEvent('installation-fee:updated', {
      bubbles: true,
      detail: { cart },
    })
  );
}

async function syncInstallationDeliveryFee() {
  if (isSyncingInstallationFee) return pendingInstallationFeeSync;

  isSyncingInstallationFee = true;

  pendingInstallationFeeSync = (async () => {
    let changed = false;

    try {
      const cart = await getInstallationFeeCart();
      const sectionsToUpdate = getCartSectionIds();
      const hasShortRental = cart.items?.some(isOneToFiveMonthRentalItem);
      const feeLines = (cart.items || [])
        .map((item, index) => ({ item, line: index + 1 }))
        .filter(({ item }) => isInstallationDeliveryFeeItem(item));
      const firstFeeLine = feeLines[0];
      const feeShouldMoveToBottom =
        hasShortRental && firstFeeLine && Number(firstFeeLine.line) !== Number(cart.items?.length || 0);
      const feeNeedsRefresh =
        hasShortRental &&
        firstFeeLine &&
        (Number(firstFeeLine.item.quantity) !== 1 || !hasExpectedInstallationFeeProperties(firstFeeLine.item) || feeShouldMoveToBottom);

      if (hasShortRental && feeLines.length === 0) {
        await addInstallationDeliveryFee();
        changed = true;
      }

      if (!hasShortRental && feeLines.length > 0) {
        for (const feeLine of [...feeLines].reverse()) {
          await changeInstallationFeeLine(feeLine.line, 0, sectionsToUpdate);
        }
        changed = true;
      }

      if (hasShortRental && feeLines.length > 1 && !feeNeedsRefresh) {
        for (let index = feeLines.length - 1; index > 0; index -= 1) {
          await changeInstallationFeeLine(feeLines[index].line, 0, sectionsToUpdate);
        }
        changed = true;
      }

      if (feeNeedsRefresh) {
        for (const feeLine of [...feeLines].reverse()) {
          await changeInstallationFeeLine(feeLine.line, 0, sectionsToUpdate);
        }
        await addInstallationDeliveryFee();
        changed = true;
      }

      if (!changed) return cart;

      const updatedCart = await getInstallationFeeCart();
      dispatchInstallationFeeCartUpdate(updatedCart);

      return updatedCart;
    } catch (error) {
      console.warn('[Installation Delivery Fee]', error);
      return null;
    } finally {
      isSyncingInstallationFee = false;
      pendingInstallationFeeSync = null;
    }
  })();

  return pendingInstallationFeeSync;
}

function scheduleInstallationDeliveryFeeSync(delay = 80) {
  window.clearTimeout(window.__installationDeliveryFeeSyncTimer);
  window.__installationDeliveryFeeSyncTimer = window.setTimeout(syncInstallationDeliveryFee, delay);
}

function protectRentalCheckoutButtons() {
  if (!document.querySelector('.rental-product-form-block')) return;

  document
    .querySelectorAll('.accelerated-checkout-block, shopify-accelerated-checkout, .shopify-payment-button')
    .forEach((element) => {
      element.setAttribute('hidden', 'true');
    });
}

document.addEventListener('DOMContentLoaded', () => {
  protectRentalCheckoutButtons();
  syncInstallationDeliveryFee();
});

window.addEventListener('pageshow', syncInstallationDeliveryFee);

document.addEventListener('cart:update', (event) => {
  if (event.detail?.data?.source === 'installation-delivery-fee-sync') return;
  scheduleInstallationDeliveryFeeSync();
});

document.addEventListener('cart:updated', () => scheduleInstallationDeliveryFeeSync());
document.addEventListener('cart:refresh', () => scheduleInstallationDeliveryFeeSync());
document.addEventListener('ajaxCart:updated', () => scheduleInstallationDeliveryFeeSync());
document.addEventListener('product:added', () => scheduleInstallationDeliveryFeeSync());
document.addEventListener('variant:update', protectRentalCheckoutButtons);
document.addEventListener('shopify:section:load', protectRentalCheckoutButtons);

document.addEventListener('change', (event) => {
  if (
    event.target?.matches?.('[name="updates[]"], input.quantity__input') ||
    event.target?.closest?.('cart-items-component, cart-drawer-component')
  ) {
    scheduleInstallationDeliveryFeeSync();
  }
});

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const feeRow = target.closest('[data-installation-delivery-fee="true"]');
    if (!feeRow) return;

    if (target.closest('.cart-items__remove, .quantity-minus, .quantity-plus')) {
      event.preventDefault();
      event.stopPropagation();
      scheduleInstallationDeliveryFeeSync();
    }
  },
  true
);

document.addEventListener(
  'submit',
  async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
    if (form.dataset.installationDeliveryFeeSynced === 'true') return;

    event.preventDefault();
    form.dataset.installationDeliveryFeeSynced = 'true';

    await syncInstallationDeliveryFee();
    window.location.assign('/checkout');
  },
  true
);

document.addEventListener(
  'click',
  async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const checkoutButton = target.closest('button[name="checkout"], #checkout, a[href="/checkout"]');
    if (!checkoutButton || checkoutButton.closest('form#cart-form')) return;

    event.preventDefault();
    await syncInstallationDeliveryFee();
    window.location.assign('/checkout');
  },
  true
);

window.XFurnitureInstallationDeliveryFee = {
  variantId: INSTALLATION_DELIVERY_FEE_VARIANT_ID,
  sync: syncInstallationDeliveryFee,
  isFeeItem: isInstallationDeliveryFeeItem,
  isShortRentalItem: isOneToFiveMonthRentalItem,
};
